export type SalesCustomer = {
  id: number;
  name: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  profilePicture?: string;
  role?: string;
  registeredOn?: string | null;
  orderCount?: number;
  paidOrderCount?: number;
  lifetimeRevenue?: number;
  outstandingBalance?: number;
  lastOrderOn?: string | null;
};

export type SalesCustomerDetail = SalesCustomer & {
  recentOrders: Array<{
    id: number;
    total: number;
    amountPaid: number;
    balance: number;
    status: string;
    addedOn: string | null;
    trackingNumber: string;
    paymentType: string;
    deliveryName: string;
  }>;
};

async function parseResponse(response: Response) {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.message || `Request failed: ${response.status}`);
  }

  return data;
}

export async function fetchSalesCustomers(query: URLSearchParams) {
  const response = await fetch(`/api/sales/customers?${query.toString()}`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as
    | {
        customers: SalesCustomer[];
      }
    | {
        customers: SalesCustomer[];
        pagination: {
          page: number;
          pageSize: number;
          totalCustomers: number;
          totalPages: number;
          hasPreviousPage: boolean;
          hasNextPage: boolean;
        };
        summary: {
          totalCustomers: number;
          customersWithOrders: number;
          activeThisMonth: number;
          paidRevenueTotal: number;
        };
      };
}

export async function fetchSalesCustomer(customerId: number) {
  const response = await fetch(`/api/sales/customers/${customerId}`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as SalesCustomerDetail;
}

export async function createSalesCustomer(input: {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
}) {
  const response = await fetch("/api/sales/customers", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return (await parseResponse(response)) as {
    status: "success";
    customer: SalesCustomer;
  };
}

export async function updateSalesCustomer(
  customerId: number,
  input: {
    firstName: string;
    middleName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    address?: string;
    profilePicture?: string;
  }
) {
  const response = await fetch(`/api/sales/customers/${customerId}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return (await parseResponse(response)) as {
    status: "success";
  };
}
